/**
 * ariveDrift — detect fields where the Origin deal disagrees with the last
 * value synced from Arive (deal.ariveLoanData, the parsed payload written by
 * the inbound sync engine).
 *
 * Used by the deal page to flag drifted fields. Loan-file financials are
 * LOS-owned (the Arive API rejects outbound pushes for them on loans), so a
 * drifted value usually means someone edited Origin directly and the value
 * will NOT reach Arive; the flag tells the user which side to trust.
 */

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `$${n.toLocaleString()}`;
};

const fmtDate = (v) => normalizeDate(v) || String(v);

/** Normalize any date-ish value to 'YYYY-MM-DD' (local parts for Date objects). */
function normalizeDate(v) {
  if (v == null || v === '') return null;
  if (typeof v?.toDate === 'function') v = v.toDate();
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return null;
    const mm = String(v.getMonth() + 1).padStart(2, '0');
    const dd = String(v.getDate()).padStart(2, '0');
    return `${v.getFullYear()}-${mm}-${dd}`;
  }
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

function numbersEqual(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
  return na === nb;
}

function stringsEqual(a, b) {
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

function zipsEqual(a, b) {
  const za = String(a).trim().slice(0, 5);
  const zb = String(b).trim().slice(0, 5);
  return za === zb;
}

function datesEqual(a, b) {
  const da = normalizeDate(a);
  const db = normalizeDate(b);
  if (!da || !db) return false;
  return da === db;
}

const COMPARATORS = { number: numbersEqual, string: stringsEqual, zip: zipsEqual, date: datesEqual };
const FORMATTERS = { money: fmtMoney, date: fmtDate };

// key: drift map key the UI looks up
// origin: (deal) => Origin value; arive: (parsed) => Arive value
const DRIFT_FIELDS = [
  {
    key: 'amount',
    label: 'Loan Amount',
    type: 'number',
    format: 'money',
    origin: (deal) => deal.amount ?? deal.loanAmount,
    arive: (a) => a.baseLoanAmount ?? a.totalLoanAmount,
  },
  {
    key: 'estimatedClosingDate',
    label: 'Est. Closing',
    type: 'date',
    format: 'date',
    origin: (deal) => deal.estimatedClosingDate,
    arive: (a) => a.keyDates?.estimatedFundingDate ?? a.keyDates?.estimatedClosingDate,
  },
  {
    key: 'lender',
    label: 'Lender',
    type: 'string',
    origin: (deal) => deal.lender,
    arive: (a) => a.lenderName,
  },
  {
    key: 'loanNumber',
    label: 'Loan Number',
    type: 'string',
    origin: (deal) => deal.loanNumber,
    arive: (a) => a.lenderLoanId,
  },
  {
    key: 'propertyStreet',
    label: 'Property Address',
    type: 'string',
    origin: (deal) => deal.propertyAddress?.street,
    arive: (a) => a.propertyStreet,
  },
  {
    key: 'propertyCity',
    label: 'City',
    type: 'string',
    origin: (deal) => deal.propertyAddress?.city,
    arive: (a) => a.propertyCity,
  },
  {
    key: 'propertyState',
    label: 'State',
    type: 'string',
    origin: (deal) => deal.propertyAddress?.state,
    arive: (a) => a.propertyState,
  },
  {
    key: 'propertyZip',
    label: 'Zip',
    type: 'zip',
    origin: (deal) => deal.propertyAddress?.zip,
    arive: (a) => a.propertyZip,
  },
];

/**
 * @param {object} deal - camelCase deal from the Supabase transform
 *   (needs ariveId + ariveLoanData).
 * @returns {object} map of driftKey -> { label, origin, arive, ariveFormatted }
 *   Empty object when the deal is not linked or nothing drifts.
 */
export function getAriveDrift(deal) {
  const arive = deal?.ariveLoanData;
  if (!deal?.ariveId || !arive || typeof arive !== 'object' || Object.keys(arive).length === 0) {
    return {};
  }

  const drift = {};
  for (const field of DRIFT_FIELDS) {
    const ariveVal = field.arive(arive);
    if (ariveVal == null || ariveVal === '') continue; // nothing on the Arive side to compare

    const originVal = field.origin(deal);
    const originEmpty = originVal == null || originVal === ''
      || (field.type === 'number' && Number(originVal) === 0);
    const hasOrigin = !originEmpty;
    const equal = hasOrigin && COMPARATORS[field.type](originVal, ariveVal);

    if (!equal) {
      const format = FORMATTERS[field.format] || ((v) => String(v));
      drift[field.key] = {
        label: field.label,
        origin: hasOrigin ? originVal : null,
        arive: ariveVal,
        ariveFormatted: format(ariveVal),
      };
    }
  }
  return drift;
}

export default getAriveDrift;
